import { spore } from '@/spore/runtime';
import { Panel } from './Panel';
import { runLabTouchTest } from './TouchTestOverlay';

const pct = (x) => (x === null || x === undefined ? '—' : `${Math.round(x * 100)}%`);

const TestResult = ({ r }) => {
  if (!r) return <div data-testid="touch-test-result" className="text-xs text-[var(--dim)]">No touch test yet — using the default detector.</div>;
  const warn = !r.applied;
  return (
    <div data-testid="touch-test-result" className={`text-xs ${warn ? 'text-[var(--amber)]' : 'text-[var(--muted)]'}`}>
      LAST TEST ({r.source}) · best <b>{r.feature}</b> · separation <b data-testid="touch-test-separation">{pct(r.separation)}</b> ({r.quality}){warn ? ` · POOR → kept ${r.kept} detector` : ' · applied'}
      {r.recordingId && <> · uploaded {r.recordingId}</>}
      <div data-testid="touch-test-hover-result" className={r.hoverMode === 'degraded' ? 'text-[var(--amber)]' : ''}>
        HOVER vs REST <b data-testid="touch-test-hover-separation">{pct(r.hoverSeparation)}</b> · HOVER vs TOUCH <b data-testid="touch-test-hover-touch-separation">{pct(r.touchHoverSeparation)}</b> · {r.hoverMode === 'tuned' ? `hover band ${r.hoverOn}–${r.hoverOff}` : r.hoverMode === 'degraded' ? 'DEGRADED: a tease accepts any touch' : 'no hover step'}
      </div>
      <div className="mt-1 flex flex-wrap gap-x-4">
        {Object.entries(r.perFeature || {}).map(([f, p]) => <span key={f} data-testid={`touch-test-feature-${f}`}>{f}: {pct(p.separation)}</span>)}
      </div>
    </div>
  );
};

export const BinaryTouchPanel = ({ snap }) => {
  const t = snap.touch5;
  const d = t.detector;
  const test = snap.touchTest;
  const up = window.__spore.uploads;
  return (
    <Panel
      testId="binary-touch-panel"
      code="00b / TOUCH"
      title="Touch Detector · Touching or Not"
      right={
        <div className="flex gap-2">
          <button data-testid="touch-test-button" className="btn-lab px-3 py-1 text-[0.65rem]" disabled={test.running} onClick={runLabTouchTest}>{test.running ? 'Testing…' : 'Touch test (C)'}</button>
          <button data-testid="relearn-baseline-button" className="btn-lab px-3 py-1 text-[0.65rem]" onClick={() => spore.relearnBaseline()}>Re-learn base (B)</button>
          <button data-testid="reset-detector-button" className="btn-lab px-3 py-1 text-[0.65rem]" onClick={() => spore.resetDetector()}>Default detector</button>
          <button data-testid="save-snapshot-button" className="btn-lab px-3 py-1 text-[0.65rem]" onClick={() => spore.saveSnapshot()}>Save 60 s (X)</button>
        </div>
      }
    >
      <div className="grid grid-cols-12 items-center gap-6">
        <div data-testid="touch-indicator" data-touching={t.touching} data-contact={t.state3} className="col-span-12 border p-6 text-center font-display text-5xl font-extrabold tracking-[0.08em] lg:col-span-6" style={t.touching ? { borderColor: '#ffd166', color: '#ffd166', boxShadow: '0 0 50px #ffd16655' } : t.hovering ? { borderColor: '#ff9ec7', color: '#ff9ec7' } : { borderColor: 'var(--hair)', color: 'var(--dim)' }}>
          {t.touching ? 'TOUCHING' : t.hovering ? `HOVERING ${(t.hoverMs / 1000).toFixed(1)} s` : 'NOT TOUCHING'}
        </div>
        <div className="col-span-6 lg:col-span-3">
          <div className="label-eyebrow">Touch duration</div>
          <div data-testid="touch-duration" className="font-display text-4xl font-bold">{(t.touchMs / 1000).toFixed(1)} s</div>
        </div>
        <div className="col-span-6 lg:col-span-3">
          <div className="label-eyebrow">Last symbol</div>
          <div data-testid="last-symbol" className="font-display text-4xl font-bold">{t.lastSymbol ? `${t.lastSymbol.symbol === 'TAP' ? '●' : t.lastSymbol.symbol === 'TEASE' ? '〰' : '▬'} ${t.lastSymbol.symbol}` : '—'}</div>
          <div className="text-xs text-[var(--muted)]">{t.lastSymbol ? `${(t.lastSymbol.durationMs / 1000).toFixed(2)} s${t.lastSymbol.ambiguous ? ' · ambiguous' : ''}` : 'TAP < 0.8 s · STROKE 1.0–4.0 s · MASSAGE ≥ 5.0 s'}</div>
        </div>
      </div>
      <div data-testid="detector-config" className="mt-4 text-sm">
        DETECTOR <b>{d.feature}</b> · on ≥ {d.thrOnScaled.toFixed(1)} · off &lt; {d.thrOffScaled.toFixed(1)} · hover ≥ {d.hoverOnScaled.toFixed(1)} (<b data-testid="detector-hover-mode">{d.hoverMode}</b>) · source <b data-testid="detector-source">{d.source}</b> · separation <b data-testid="detector-separation">{pct(d.separation)}</b> · sensitivity
        <input data-testid="sensitivity-slider" className="ml-2 align-middle" type="range" min="1" max="5" value={t.sensitivity} onChange={(e) => spore.setSensitivity(Number(e.target.value))} /> {t.sensitivity}/5
      </div>
      <div data-testid="detector-live" className="mt-1 text-xs text-[var(--muted)]">
        value now {t.value.toFixed(1)} · resting CC90 {t.rest ? t.rest.cc.toFixed(0) : '—'} · CC now {t.features.cc ?? '—'} · auto re-baselines {t.rebaselines} · uploads {up.count}
      </div>
      <div className="mt-3"><TestResult r={test.result} /></div>
    </Panel>
  );
};
